import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { FolderKanban, Receipt, CheckCircle2, Clock, ArrowRight, Sparkles } from "lucide-react";
import { clientApi } from "@/lib/api";

const STATUS_LABEL = { active: "En cours", completed: "Terminé", on_hold: "En pause" };
const STATUS_COLOR = { active: "bg-blue-100 text-blue-700", completed: "bg-green-100 text-green-700", on_hold: "bg-gray-100 text-gray-700" };

export default function ClientDashboard() {
  const { me } = useOutletContext();
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    Promise.all([clientApi.get("/client/projects"), clientApi.get("/client/invoices")])
      .then(([p, i]) => { setProjects(p.data); setInvoices(i.data); });
  }, []);

  const pendingDeliv = projects.flatMap((p) => (p.deliverables || []).filter((d) => d.status === "pending").map((d) => ({ ...d, project: p })));
  const totalPending = invoices.filter((i) => i.status === "sent").reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div className="space-y-8" data-testid="client-dashboard">
      <div>
        <h1 className="font-heading font-black text-3xl text-[#0B1F3A]">Bonjour {me.name.split(" ")[0]} 👋</h1>
        <p className="text-[#4B5563] mt-1">Voici un aperçu de vos projets et factures.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <FolderKanban className="h-5 w-5 text-[#00B8D9]" />
          <div className="text-3xl font-heading font-extrabold text-[#0B1F3A] mt-2">{projects.length}</div>
          <div className="text-sm text-[#4B5563]">Projet(s) actif(s)</div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <Sparkles className="h-5 w-5 text-orange-500" />
          <div className="text-3xl font-heading font-extrabold text-[#0B1F3A] mt-2">{pendingDeliv.length}</div>
          <div className="text-sm text-[#4B5563]">Livrable(s) à valider</div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <Receipt className="h-5 w-5 text-[#00B8D9]" />
          <div className="text-3xl font-heading font-extrabold text-[#0B1F3A] mt-2">{invoices.length}</div>
          <div className="text-sm text-[#4B5563]">Facture(s)</div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <Clock className="h-5 w-5 text-orange-500" />
          <div className="text-3xl font-heading font-extrabold text-[#0B1F3A] mt-2">{totalPending.toLocaleString("fr-CA", { maximumFractionDigits: 0 })} $</div>
          <div className="text-sm text-[#4B5563]">En attente</div>
        </div>
      </div>

      {pendingDeliv.length > 0 && (
        <div className="bg-[#00B8D9]/5 border-l-4 border-[#00B8D9] rounded-r-xl p-5">
          <div className="text-[#00B8D9] text-xs tracking-[0.18em] uppercase font-bold mb-2">Action requise</div>
          <div className="font-heading font-bold text-[#0B1F3A]">{pendingDeliv.length} livrable(s) attendent votre validation</div>
          <div className="mt-3 space-y-2">
            {pendingDeliv.slice(0, 3).map((d) => (
              <Link key={d.id} to={`/portail/projets/${d.project.id}`} className="flex items-center justify-between bg-white rounded-lg p-3 border border-gray-200 hover:border-[#00B8D9] transition-colors">
                <div>
                  <div className="font-semibold text-[#0B1F3A] text-sm">{d.title}</div>
                  <div className="text-xs text-[#4B5563]">{d.project.name}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-[#00B8D9]" />
              </Link>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-bold text-xl text-[#0B1F3A]">Mes projets récents</h2>
          <Link to="/portail/projets" className="text-sm text-[#00B8D9] font-semibold hover:underline">Voir tout →</Link>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {projects.length === 0 && <div className="md:col-span-2 bg-white rounded-xl p-8 text-center text-[#4B5563] border border-gray-200">Aucun projet pour l'instant.</div>}
          {projects.slice(0, 4).map((p) => {
            const totalTasks = (p.tasks || []).length;
            const doneTasks = (p.tasks || []).filter((t) => t.status === "done").length;
            const progress = totalTasks ? Math.round((doneTasks / totalTasks) * 100) : 0;
            return (
              <Link key={p.id} to={`/portail/projets/${p.id}`} data-testid={`portal-project-${p.id}`} className="block bg-white rounded-xl p-5 border border-gray-200 hover:border-[#00B8D9] hover:shadow-md transition-all">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-heading font-bold text-[#0B1F3A]">{p.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_COLOR[p.status] || STATUS_COLOR.active}`}>{STATUS_LABEL[p.status] || p.status}</span>
                </div>
                <p className="text-sm text-[#4B5563] line-clamp-2 mb-3">{p.description || "—"}</p>
                <div className="flex items-center gap-2 text-xs text-[#4B5563]">
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#00B8D9]" style={{ width: `${progress}%` }} />
                  </div>
                  <span className="font-semibold text-[#0B1F3A]">{progress}%</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
